<script>
  import { onMount } from 'svelte';
  import { getApiUrl } from '../api';
  import { authFetch } from '../authService';

  let promotions = [];
  let isLoading = true;
  let error = '';
  
  // Pagination
  let page = 1;
  const pageSize = 10;
  let totalPages = 1;

  // SEO Pre-render State
  let isRefreshingGuide = false;
  let refreshGuideResult = null;
  let refreshGuideError = '';
  let guideStatus = null;

  onMount(() => {
    fetchPromotions();
    fetchGuideStatus();
  });

  async function fetchGuideStatus() {
    try {
      const res = await authFetch(getApiUrl('/admin/tax-guide/status'));
      if (res.ok) {
        guideStatus = await res.json();
      }
    } catch (e) {
      // Non-critical, ignore
    }
  }

  async function refreshTaxGuide() {
    isRefreshingGuide = true;
    refreshGuideResult = null;
    refreshGuideError = '';
    try {
      const res = await authFetch(getApiUrl('/admin/tax-guide/regenerate'), {
        method: 'POST'
      });
      const data = await res.json();
      if (!res.ok) {
        throw new Error(data.error || 'Failed to regenerate tax guide');
      }
      refreshGuideResult = data;
      guideStatus = {
        isGenerated: true,
        lastUpdated: data.timestamp,
        promotionsCount: data.promotionsCount,
        year: '2026/2027'
      };
    } catch (e) {
      refreshGuideError = e.message;
    } finally {
      isRefreshingGuide = false;
    }
  }

  async function fetchPromotions() {
    isLoading = true;
    error = '';
    try {
      const res = await authFetch(getApiUrl(`/admin/promotions?page=${page}&pageSize=${pageSize}`));
      if (!res.ok) throw new Error("Failed to load promotions");
      
      const data = await res.json();
      promotions = data.data;
      totalPages = data.totalPages;
    } catch (e) {
      error = e.message;
    } finally {
      isLoading = false;
    }
  }

  function nextPage() {
      if (page < totalPages) {
          page++;
          fetchPromotions();
      }
  }

  function prevPage() {
      if (page > 1) {
          page--;
          fetchPromotions();
      }
  }
</script>

<div class="admin-container">

  <!-- SEO & AI Overview Pre-render Control Card -->
  <div class="seo-card">
    <div class="seo-card-header">
      <div class="seo-title-group">
        <span class="seo-badge">SEO & AI Overviews</span>
        <h3 class="seo-title">2026/2027 Tax Rebate Guide Pre-rendering</h3>
      </div>
      <button 
        class="btn-refresh" 
        on:click={refreshTaxGuide} 
        disabled={isRefreshingGuide}
      >
        {#if isRefreshingGuide}
          <span class="spinner"></span> 正在重新生成 / Regenerating...
        {:else}
          🔄 Refresh 2026/2027 Pre-rendered Tax Guide (ZH & EN)
        {/if}
      </button>
    </div>

    <p class="seo-desc">
      一鍵從資料庫即時提取最新「交稅回贈 (Tax Rebate)」推廣，自動重新生成繁體中文及英文雙語靜態 HTML 頁面及結構化數據 (Schema.org / FAQPage)。Google AI Overviews、Perplexity 及 ChatGPT 將直接引用此頁面。
    </p>

    {#if guideStatus}
      <div class="guide-status-bar">
        <span>📅 稅季年度: <strong>{guideStatus.year || '2026/2027'}</strong></span>
        {#if guideStatus.lastUpdated}
          <span>⏱️ 上次更新: <strong>{new Date(guideStatus.lastUpdated).toLocaleString()}</strong></span>
        {/if}
        {#if guideStatus.promotionsCount !== undefined}
          <span>📊 資料庫交稅推廣: <strong>{guideStatus.promotionsCount} 個</strong></span>
        {/if}
      </div>
    {/if}

    {#if refreshGuideResult}
      <div class="alert alert-success">
        ✅ {refreshGuideResult.message} 
        {#if refreshGuideResult.promotionsCount !== undefined}
          (已同步 {refreshGuideResult.promotionsCount} 個有效推廣至靜態頁面)
        {/if}
      </div>
    {/if}

    {#if refreshGuideError}
      <div class="alert alert-error">
        ❌ {refreshGuideError}
      </div>
    {/if}

    <div class="seo-links">
      <a href="/tax-rebate-promotions.html" target="_blank" rel="noopener noreferrer" class="seo-link">
        🔗 查看繁體中文指南 (/tax-rebate-promotions.html)
      </a>
      <a href="/en/tax-rebate-promotions.html" target="_blank" rel="noopener noreferrer" class="seo-link">
        🔗 View English Guide (/en/tax-rebate-promotions.html)
      </a>
      <a href="/tax-calculator" target="_blank" rel="noopener noreferrer" class="seo-link-calc">
        🧮 前往 SNAP PERK 計算機
      </a>
    </div>
  </div>

  <h2>All Promotions (Raw Data)</h2>
  
  <div class="pagination-controls">
      <button on:click={prevPage} disabled={page === 1}>Previous</button>
      <span>Page {page} of {totalPages}</span>
      <button on:click={nextPage} disabled={page === totalPages}>Next</button>
  </div>

  {#if isLoading}
    <p>Loading...</p>
  {:else if error}
    <p class="error">{error}</p>
  {:else}
    <div class="json-list">
        {#each promotions as promo}
            <div class="json-card">
                <pre>{JSON.stringify(promo, null, 2)}</pre>
            </div>
        {/each}
    </div>
  {/if}
</div>

<style>
  .admin-container { max-width: 1000px; margin: 2rem auto; padding: 0 1rem; }
  h2 { margin: 2rem 0 1rem; }

  /* SEO Card Styling */
  .seo-card {
    background: #ffffff;
    border: 1px solid #e2e8f0;
    border-radius: 12px;
    padding: 1.5rem;
    margin-bottom: 2rem;
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.05);
  }
  .seo-card-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    flex-wrap: wrap;
    gap: 1rem;
    margin-bottom: 0.75rem;
  }
  .seo-title-group {
    display: flex;
    flex-direction: column;
    gap: 0.35rem;
  }
  .seo-badge {
    display: inline-block;
    align-self: flex-start;
    font-size: 0.75rem;
    font-weight: 700;
    text-transform: uppercase;
    background: #e0f2fe;
    color: #0369a1;
    padding: 0.2rem 0.5rem;
    border-radius: 4px;
    letter-spacing: 0.5px;
  }
  .seo-title {
    margin: 0;
    font-size: 1.35rem;
    font-weight: 700;
    color: #0f172a;
  }
  .seo-desc {
    color: #475569;
    font-size: 0.95rem;
    line-height: 1.5;
    margin-bottom: 1rem;
  }
  .btn-refresh {
    background: linear-gradient(135deg, #2563eb, #1d4ed8);
    color: white;
    font-size: 0.95rem;
    font-weight: 600;
    padding: 0.65rem 1.25rem;
    border-radius: 8px;
    border: none;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(37, 99, 235, 0.25);
    transition: all 0.15s ease;
    display: inline-flex;
    align-items: center;
    gap: 0.5rem;
  }
  .btn-refresh:hover:not(:disabled) {
    background: linear-gradient(135deg, #1d4ed8, #1e40af);
    transform: translateY(-1px);
    box-shadow: 0 4px 8px rgba(37, 99, 235, 0.35);
  }
  .btn-refresh:disabled {
    background: #94a3b8;
    cursor: not-allowed;
    transform: none;
  }
  .guide-status-bar {
    display: flex;
    flex-wrap: wrap;
    gap: 1.5rem;
    background: #f8fafc;
    border: 1px solid #edf2f7;
    padding: 0.6rem 1rem;
    border-radius: 6px;
    font-size: 0.85rem;
    color: #475569;
    margin-bottom: 1rem;
  }
  .alert {
    padding: 0.75rem 1rem;
    border-radius: 6px;
    font-size: 0.9rem;
    margin-bottom: 1rem;
  }
  .alert-success {
    background: #ecfdf5;
    color: #065f46;
    border: 1px solid #a7f3d0;
  }
  .alert-error {
    background: #fef2f2;
    color: #991b1b;
    border: 1px solid #fecaca;
  }
  .seo-links {
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    align-items: center;
    border-top: 1px solid #f1f5f9;
    padding-top: 1rem;
  }
  .seo-link {
    font-size: 0.85rem;
    color: #2563eb;
    text-decoration: none;
    padding: 0.35rem 0.75rem;
    background: #eff6ff;
    border-radius: 6px;
    transition: background 0.15s;
  }
  .seo-link:hover {
    background: #dbeafe;
    text-decoration: underline;
  }
  .seo-link-calc {
    font-size: 0.85rem;
    color: #059669;
    text-decoration: none;
    padding: 0.35rem 0.75rem;
    background: #ecfdf5;
    border-radius: 6px;
    margin-left: auto;
    font-weight: 600;
  }
  .seo-link-calc:hover {
    background: #d1fae5;
  }
  
  .pagination-controls {
      display: flex;
      justify-content: space-between;
      align-items: center;
      margin-bottom: 1rem;
      background: #f8f9fa;
      padding: 1rem;
      border-radius: 8px;
      border: 1px solid #eee;
  }
  
  button {
      padding: 0.5rem 1rem;
      background: #007bff;
      color: white;
      border: none;
      border-radius: 4px;
      cursor: pointer;
  }
  button:disabled { background: #ccc; cursor: not-allowed; }

  .json-list {
      display: flex;
      flex-direction: column;
      gap: 1rem;
  }
  
  .json-card {
      background: #f4f4f4;
      padding: 1rem;
      border-radius: 8px;
      overflow-x: auto;
      border: 1px solid #ddd;
  }
  
  pre {
      margin: 0;
      font-family: monospace;
      font-size: 0.85rem;
      color: #333;
  }
  
  .error { color: red; }
</style>
